import {gql} from '@apollo/client';
import {objectFromEntries} from '@benchling/js-utils';

import {PolymerSubtype} from 'constants/MolbioConstants';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {BiointelligentLinkType} from 'molbio/biointelligent-links/BiointelligentLinkConstants';
import {StepItemStatus} from 'ui-library/StepNav';

export interface DiffDetails {
  ops: Array<unknown>;
  alignedA: string;
  alignedB: string;
}

export interface EditEffects {
  [ReplaceWarningInfoKey.OVERLAPPING_LINKS]: number;
  [ReplaceWarningInfoKey.REMOVED_FEATURES]: number;
  [ReplaceWarningInfoKey.MUTATED_FEATURES]: number;
  // TODO: add keys for bocking issues (range edit affects mods, missing permissions, nested links)
}

export interface ReplaceLinkModalReducerState {
  currentStepKey: ReplaceLinkStepKey;
  sidebarExpanded: boolean;
  statusByStep: {[key in ReplaceLinkStepKey]: StepItemStatus | null};
  selectedContainerPolymerIds: Array<string>;
  editEffectsByContainerPolymerId: {
    [polymerId: string]: EditEffects;
  };
  diffDetails: DiffDetails | null;
}

export enum ReplaceLinkStepKey {
  REVIEW_EDITS = 'REVIEW_EDITS',
  SELECT_SEQUENCES = 'SELECT_SEQUENCES',
  CONFIRM = 'CONFIRM',
}

export const ORDERED_STEPS: Array<ReplaceLinkStepKey> = [
  ReplaceLinkStepKey.REVIEW_EDITS,
  ReplaceLinkStepKey.SELECT_SEQUENCES,
  ReplaceLinkStepKey.CONFIRM,
];

export const UNVISITED_STEPS_STATUSES: {[key in ReplaceLinkStepKey]: StepItemStatus} = objectFromEntries(
  ORDERED_STEPS.map((step: ReplaceLinkStepKey) => [step, StepItemStatus.UNVISITED])
) as {[key in ReplaceLinkStepKey]: StepItemStatus};

export const INITIAL_REDUCER_STATE: ReplaceLinkModalReducerState = {
  currentStepKey: ReplaceLinkStepKey.REVIEW_EDITS,
  sidebarExpanded: true,
  statusByStep: UNVISITED_STEPS_STATUSES,
  selectedContainerPolymerIds: [],
  diffDetails: null,
  editEffectsByContainerPolymerId: {},
};

// Expose as function for testing.
export function getInitialReducerstate(): ReplaceLinkModalReducerState {
  return INITIAL_REDUCER_STATE;
}

export const COMPONENT_POLYMER_DETAILS_QUERY = gql`
  query ComponentPolymerDetailsQuery($id: StrictString!) @skipAudit @engComponent(name: MOLBIO) {
    polymer(id: $id) {
      ... on SequenceNode {
        id
        name
        sequenceType
        nucleotideType
      }
      ... on ProteinNode {
        id
        name
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

// Oligos can be used as neither containers nor components in the current biointelligent links.
export type BiointelligentLinkPolymerSubtype = Exclude<
  PolymerSubtype,
  PolymerSubtype.dna_oligo | PolymerSubtype.rna_oligo
>;

// Translation links cannot be replaced right now, because the back-translation is not 1:1.
export type ReplaceableBiointelligentLinkType = Exclude<
  BiointelligentLinkType,
  BiointelligentLinkType.TRANSLATION
>;

export enum ReplaceWarningInfoKey {
  OVERLAPPING_LINKS = 'OVERLAPPING_LINKS',
  REMOVED_FEATURES = 'REMOVED_FEATURES',
  MUTATED_FEATURES = 'MUTATED_FEATURES',
}
